import { create } from 'zustand';
import type { BadgeStage } from '../utils/transcodeBadge';

/**
 * 트랜스코딩(게재 준비) 진행률/상태 스토어.
 *
 * 서버는 /ws 채널로 transcode_progress / transcode_complete / transcode_failed 메시지를 보낸다
 * (업로더의 moPeerHash 대상). App.tsx의 handleWebSocketMessage가 이 스토어로 dispatch 하면,
 * Basic/MoFile 카드·FileDetailModal·Dashboard 의 구독자가 즉시 갱신된다.
 *
 * key: `${fileHash}::${resolution ?? 'main'}`
 *
 * 두 출처(WS 푸시 vs 목록/overview 의 DB 값)를 합치는 규칙 — newest-wins:
 *  - 종료 상태(done/failed)는 진행 이벤트로 덮어쓰지 않는다(순서 뒤집힘 방어).
 *  - 같은 stage 안에서 % 는 내려가지 않는다(재전송·2초 스로틀 DB 값 vs 신선한 WS 값 충돌 방어).
 *  - seed(DB 값)는 "10초 내 WS 갱신이 있는 진행 중 항목"을 덮지 않는다. 종료 상태는 항상 받는다.
 *  - stage(prepare|queued|encode)는 WS 로만 온다. 새로고침 후엔 서버의 preparing 플래그로 보강.
 */

export type TranscodeStatus = 'pending' | 'processing' | 'done' | 'failed';

export interface TranscodeStateEntry {
  status: TranscodeStatus;
  progress: number;        // 0-100
  etaSec: number | null;
  resolution: string | null;  // null = 대표
  error?: string;
  // 완료 시 서버가 알려준 메타
  codec?: string;
  fps?: number;
  durationSec?: number;
  // 단계(대표만). prepare=사진→영상, queued=큐 대기, encode=인코딩.
  stage?: BadgeStage;
  // 이 파일이 prepare 단계를 거쳤는가(사진 출신) — "2/2" 라벨 근거.
  hadPrepare?: boolean;
  // WS 이벤트를 받은 적이 있는가 / 마지막 WS 이벤트 시각 — 지연 판정(배지 유틸)용.
  sawWs?: boolean;
  lastEventAt?: number;
  // 마지막 업데이트 시각
  updatedAt: number;
}

const keyOf = (fileHash: string, resolution: string | null): string =>
  `${fileHash}::${resolution ?? 'main'}`;

const isTerminal = (s?: TranscodeStatus | string | null) => s === 'done' || s === 'failed';
const SEED_GRACE_MS = 10_000;

interface FileTranscodeState {
  entries: Map<string, TranscodeStateEntry>;

  /** 진행률 푸시 처리 */
  applyProgress: (
    fileHash: string,
    resolution: string | null,
    payload: { percent: number; etaSec: number | null; stage?: BadgeStage | null }
  ) => void;
  /** 완료 푸시 처리 */
  applyComplete: (
    fileHash: string,
    resolution: string | null,
    payload: { codec?: string; fps?: number; durationSec?: number }
  ) => void;
  /** 실패 푸시 처리 */
  applyFailed: (
    fileHash: string,
    resolution: string | null,
    error: string
  ) => void;
  /** 목록/overview/모달의 DB 값 반영(newest-wins). preparing=true 면 stage=prepare 로 보강. */
  seed: (
    fileHash: string,
    resolution: string | null,
    entry: Partial<TranscodeStateEntry> & { status: TranscodeStatus; preparing?: boolean | null }
  ) => void;
  /** 조회 (구독은 selector로) */
  get: (fileHash: string, resolution: string | null) => TranscodeStateEntry | undefined;
  /** WS 가 끊겼다 — 지연 판정을 멈춘다(새 탭이 열리면 서버가 구 탭 소켓을 닫는 구조라 구 탭 오탐 방지). */
  markWsClosed: () => void;
  /** WS 재연결/복귀 시 호출 — 진행 중(non-terminal) 항목의 실제 상태를 서버에서 재조회해
   *  화해. transcode_complete WS 푸시를 재연결 갭(서버 재시작 등)에 놓쳐 진행률 바가
   *  얼어붙는 것을 복구한다. (WS 는 전달 보장/replay 가 없음.) */
  reconcileStale: () => Promise<void>;
}

export const useFileTranscodeStore = create<FileTranscodeState>((set, getState) => ({
  entries: new Map(),

  applyProgress: (fileHash, resolution, { percent, etaSec, stage }) => {
    const map = new Map(getState().entries);
    const k = keyOf(fileHash, resolution);
    const prev = map.get(k);
    if (prev && isTerminal(prev.status)) return;  // 완료 뒤 늦게 온 진행 이벤트는 무시
    const st: BadgeStage | undefined = stage ?? (resolution === null ? (prev?.stage ?? 'encode') : undefined);
    const sameStage = prev?.stage === st;
    const now = Date.now();
    map.set(k, {
      status: st === 'queued' ? 'pending' : 'processing',
      progress: sameStage && prev ? Math.max(prev.progress, percent) : percent,
      etaSec,
      resolution,
      codec: prev?.codec,
      fps: prev?.fps,
      durationSec: prev?.durationSec,
      stage: st,
      hadPrepare: !!prev?.hadPrepare || st === 'prepare',
      sawWs: true,
      lastEventAt: now,
      updatedAt: now,
    });
    set({ entries: map });
  },

  applyComplete: (fileHash, resolution, payload) => {
    const map = new Map(getState().entries);
    const k = keyOf(fileHash, resolution);
    const prev = map.get(k);
    const now = Date.now();
    map.set(k, {
      status: 'done',
      progress: 100,
      etaSec: 0,
      resolution,
      codec: payload.codec,
      fps: payload.fps,
      durationSec: payload.durationSec,
      hadPrepare: prev?.hadPrepare,
      sawWs: true,
      lastEventAt: now,
      updatedAt: now,
    });
    set({ entries: map });
  },

  applyFailed: (fileHash, resolution, error) => {
    const map = new Map(getState().entries);
    const k = keyOf(fileHash, resolution);
    const prev = map.get(k);
    const now = Date.now();
    map.set(k, {
      status: 'failed',
      progress: prev?.progress ?? 0,
      etaSec: null,
      resolution,
      error,
      hadPrepare: prev?.hadPrepare,
      sawWs: true,
      lastEventAt: now,
      updatedAt: now,
    });
    set({ entries: map });
  },

  seed: (fileHash, resolution, entry) => {
    const map = new Map(getState().entries);
    const k = keyOf(fileHash, resolution);
    const prev = map.get(k);
    const now = Date.now();
    if (prev && !isTerminal(entry.status)) {
      // 진행 중 값으로는 (a) 종료 상태를 되돌리지 않고 (b) 방금 WS 로 갱신된 항목을 덮지 않는다.
      if (isTerminal(prev.status)) return;
      if (prev.sawWs && now - prev.updatedAt < SEED_GRACE_MS) return;
    }
    const stage: BadgeStage | undefined = entry.stage
      ?? (entry.preparing ? 'prepare' : (entry.status === 'pending' ? 'queued' : prev?.stage));
    const incoming = entry.progress ?? 0;
    const progress = prev && !isTerminal(entry.status) && prev.stage === stage ? Math.max(prev.progress, incoming) : incoming;
    map.set(k, {
      status: entry.status,
      progress: entry.status === 'done' ? 100 : progress,
      etaSec: entry.etaSec ?? null,
      resolution,
      codec: entry.codec ?? prev?.codec,
      fps: entry.fps ?? prev?.fps,
      durationSec: entry.durationSec ?? prev?.durationSec,
      error: entry.error ?? (entry.status === 'failed' ? prev?.error : undefined),
      stage,
      hadPrepare: !!prev?.hadPrepare || !!entry.hadPrepare || !!entry.preparing,
      sawWs: prev?.sawWs ?? false,
      lastEventAt: prev?.lastEventAt,
      updatedAt: now,
    });
    set({ entries: map });
  },

  get: (fileHash, resolution) => getState().entries.get(keyOf(fileHash, resolution)),

  markWsClosed: () => {
    const map = new Map<string, TranscodeStateEntry>();
    let changed = false;
    for (const [k, e] of getState().entries) {
      if (e.sawWs) { map.set(k, { ...e, sawWs: false }); changed = true; }
      else map.set(k, e);
    }
    if (changed) set({ entries: map });
  },

  reconcileStale: async () => {
    // 진행 중(non-terminal) 항목의 file_hash 수집
    const fileHashes = new Set<string>();
    for (const [k, e] of getState().entries) {
      if (e.status === 'pending' || e.status === 'processing') {
        fileHashes.add(k.split('::')[0]);
      }
    }
    if (fileHashes.size === 0) return;
    const seed = getState().seed;
    for (const fileHash of fileHashes) {
      try {
        // 대표: 상태·진행률·준비 마커까지 — 종료뿐 아니라 진행 중 값도 반영(백그라운드 탭 복귀 시 최신화).
        const m = await fetch(`/mofile/${fileHash}/transcode-status`, { credentials: 'include' });
        if (m.ok) {
          const d = (await m.json()).data;
          if (d?.transcode_status) {
            seed(fileHash, null, {
              status: d.transcode_status,
              progress: d.transcode_status === 'done' ? 100 : (Number(d.transcode_progress) || 0),
              preparing: !!d.preparing,
              error: d.transcode_error ?? undefined,
            });
          }
        }
        const r = await fetch(`/mofile/${fileHash}/variants`, { credentials: 'include' });
        if (!r.ok) continue;
        const fresh: Array<{ kind: string; resolution: string | null; transcode_status: TranscodeStatus; transcode_progress: number | null }> =
          (await r.json()).data || [];
        for (const v of fresh) {
          if (v.kind !== 'variant') continue;
          seed(fileHash, v.resolution, {
            status: v.transcode_status,
            progress: v.transcode_status === 'done' ? 100 : (v.transcode_progress ?? 0),
          });
        }
      } catch { /* ignore */ }
    }
  },
}));

/** Selector hook — 특정 파일/변형의 상태만 구독 */
export const useTranscodeEntry = (fileHash: string, resolution: string | null = null) =>
  useFileTranscodeStore(s => s.entries.get(keyOf(fileHash, resolution)));
